import {Vector, SierraCanvas, ColorGradient} from '@teemill/common/classes';

import {
  hsvToHex,
  stringContainsEmojis,
  splitStringByEmojis,
  removeEmojis,
  rgbToHex,
  getTextDirection,
} from '@teemill/utilities';

import {fontLoader, isFontAvailable} from '@teemill/common/helpers';

import {OmniObject} from './';

import {Mathf} from '../omnicanvas.utilities';

import distressCloudImage from './assets/distress-cloud.png';

export default class OmniText extends OmniObject {
  className = 'Text';
  extrusion = 0;
  extrusionColors = ['red'];
  extrusionDirection = new Vector(1, 1);

  fill = true;
  stroke = false;
  strokeWidth = 4;
  strokeColor = '';

  directionalBlur = new Vector(0, 0);

  distressed = false;
  distressImage = null;

  gradientDirection = 'X';

  textDirection = 'ltr';
  maxWidth = 0;
  enforceMaxWidth = false;

  curvature = 0;

  constructor(opts) {
    super();
    if (opts) {
      Object.keys(opts).forEach(i => {
        try {
          this[i] = opts[i];
          // eslint-disable-next-line
        } catch (e) {}
      });
    }

    if (this.extrusionDirection) {
      this.extrusionDirection = new Vector(
        this.extrusionDirection.x || 0,
        this.extrusionDirection.y || 0
      );
    }

    this.formatExtrusionColors();

    if (this.directionalBlur) {
      this.directionalBlur = new Vector(
        this.directionalBlur.x || 0,
        this.directionalBlur.y || 0
      );
    }

    if (this.gradient && !(this.gradient instanceof ColorGradient)) {
      this.gradient = new ColorGradient(this.gradient);
    }
  }

  /**
   * Ensures that extrusion colors are always a valid array
   */
  formatExtrusionColors() {
    if (!Array.isArray(this.extrusionColors)) {
      if (typeof this.extrusionColors === 'object') {
        this.extrusionColors = Object.values(this.extrusionColors);
      } else if (typeof this.extrusionColors === 'string') {
        this.extrusionColors = [this.extrusionColors];
      } else {
        this.extrusionColors = [];
      }
    }
  }

  /**
   * This occurs after a Text element is added to the canvas. It is used to
   * create an instance of a text editor
   *
   * @return {void}
   */
  afterInit() {
    this.createInternalCanvases();
    this.processLines();

    if (!this.fontSize && this.height) {
      this.fontSize = this.height;
    }
    for (let i = 0, l = this.lines.length; i < l; ++i) {
      if (typeof this.lines[i] !== 'string') {
        this.lines[i] = '';
      }
    }
    this.createHiddenTextEditor();

    const fontName = this.font.replace(/"/g, '');

    if (isFontAvailable(fontName)) {
      this._fontLoaded = true;
      this.controller.eventBus?.emit('font-loaded', this.font);
    } else {
      this._fontLoaded = false;

      window.$eventBus.once('webfonts-loaded', () => {
        this._fontLoaded = true;
        this.controller?.eventBus?.emit('font-loaded', this.font);
        this.controller?.render?.();
      });

      fontLoader([fontName]);
      /**
       * In the event that a font fails to load, or is very slow to load, assume the
       * font is never going to load and therefore treat as loaded
       */
      setTimeout(() => {
        if (!this._fontLoaded) {
          this._fontLoaded = true;
          this.controller?.eventBus?.emit('font-loaded', this.font);
          this.controller?.render?.();
        }
      }, 1000);
    }

    this.hiddenTextEditor.style.cssText = `font-family: ${this.font} !important;`;
  }

  createInternalCanvases() {
    const stage = this.controller?.renderer?.outputCanvas;
    const scale = this.canvasScale;

    this.outputCanvas = new SierraCanvas(
      stage.width * scale,
      stage.height * scale
    );

    this.baseLayerCanvas = new SierraCanvas(
      this.outputCanvas.width,
      this.outputCanvas.height
    );

    this.temporaryCanvas = new SierraCanvas(
      this.outputCanvas.width,
      this.outputCanvas.height
    );
  }

  resizeInternalCanvases() {
    const isExporting = this.controller?.params?.exportMode;
    const stage = isExporting
      ? this.controller?.boundingBox
      : this.controller?.renderer?.outputCanvas;
    const scale = this.canvasScale;

    this.baseLayerCanvas.resizeCanvas(
      Math.max(stage.width, stage.height) * scale,
      Math.max(stage.width, stage.height) * scale
    );
    this.outputCanvas.resizeCanvas(
      this.baseLayerCanvas.width,
      this.baseLayerCanvas.height
    );
    this.temporaryCanvas.resizeCanvas(
      this.baseLayerCanvas.width,
      this.baseLayerCanvas.height
    );
  }

  /**
   * To minimise the memory impact of this element's internal canvases, we can shrink them to 1 by 1 pixels
   * This can be done after rendering so that only one text element can have a significant memory impact
   * at any given time
   */
  shrinkInternalCanvases() {
    this.baseLayerCanvas.resizeCanvas(1, 1);
    this.outputCanvas.resizeCanvas(1, 1);
    this.temporaryCanvas.resizeCanvas(1, 1);
  }

  processLines() {
    if (typeof this.text === 'number') {
      this.text = this.text.toString();
    }
    if (typeof this.lines === 'object' && !Array.isArray(this.lines)) {
      this.updateLines(Object.values(this.lines));
    }

    if (!Array.isArray(this.lines)) {
      this.lines = [];
    }

    if (!this.lines.length) {
      this.lines.push(this.text);
    }

    this.lines.forEach((line, index) => {
      if (typeof line === 'number') {
        this.lines[index] = line.toString();
      }
    });
  }

  /**
   * Each text element has a hidden textarea that is linked to it. When you add
   * text to a text element you are actually adding text to the textarea. This
   * function initialises the textarea.
   *
   * @return {void}
   */
  createHiddenTextEditor() {
    this.hiddenTextEditor = document.createElement('textarea');
    this.hiddenTextEditor.classList.add('omnicanvas-textEditor');
    // eslint-disable-next-line
    const master = this;
    this.hiddenTextEditor.oninput = e => {
      master.onInput(e);
    };
    this.hiddenTextEditor.onkeyup = e => {
      this.textDirection = getTextDirection(this.hiddenTextEditor.value);

      master.onKeyUp(e);
    };
    this.hiddenTextEditor.value = this.lines.join('\n');
    document.body.appendChild(this.hiddenTextEditor);
  }

  /**
   * Whenever this text element is selected, reposition the hidden text editor
   * so that it hovers over the text element. This means that on mobile the
   * virtual keyboard shouldn't obscure the element and the screen will focus to
   * the element
   *
   * @return {void}
   */
  activateTextEditor() {
    let offset;
    try {
      offset = this.cnv.getBoundingClientRect();
    } catch (error) {
      offset = {
        top: 0,
        left: 0,
      };
    }
    const pixelScale = 1 / (this.controller?.getPixelScale?.() ?? 1);
    this.hiddenTextEditor.value = this.text;
    this.hiddenTextEditor.style.cssText = `
      position: absolute;
      top: ${
        offset.top +
        (this.controller?.boundingBox?.position?.y ??
          0 + this.position.y + this.height / 2) *
          pixelScale
      }px;
      left: ${
        offset.left +
        (this.controller?.boundingBox?.position?.x ??
          0 + this.position.x + this.width / 2) *
          pixelScale
      }px;
      pointer-events: none;
      paddingｰtop: ${this.fontSize}px;
      ${
        !this.controller?.params?.debugMode
          ? 'z-index: -999; height: 1px;  width: 1px; font-size: 16px; line-height: 1px;'
          : ''
      }`;
    this.hiddenTextEditor.focus();
  }

  /**
   * Deselect the hidden text editor
   *
   * @return {void}
   */
  deactivateTextEditor() {
    this.hiddenTextEditor.blur();
    this.hiddenTextEditor.selectionStart = 0;
    this.hiddenTextEditor.selectionEnd = 0;
  }

  /**
   * Override the default rotation handler - we will handle this ourselves
   */
  applyRotation() {}

  /**
   * Draw the text element onto the canvas
   *
   * @return {void}
   */
  draw() {
    if (this._fontLoaded && this.text && this.text.length) {
      this.drawText();

      if (
        this.editMode &&
        this.cursorPosition !== false &&
        !this.controller?.params?.exportMode
      ) {
        this.drawCursor();
      }
    }
  }

  get boundOffsetPosition() {
    const isExporting = this.controller?.params?.exportMode;
    if (isExporting) {
      return this.position.copy();
    }

    const boundingBox = this.controller?.boundingBox;

    return this.position.translate(
      boundingBox.position.x,
      boundingBox.position.y
    );
  }

  get relativeFontScale() {
    // 100 is an arbitrary reference font size used to assess this objects relative scale
    return this.fontSize / 100;
  }

  drawText() {
    this.resizeInternalCanvases();

    this.drawBaseText(this.baseLayerCanvas, this.fill, this.stroke);

    if (this.gradient) {
      this.applyGradientFill();
    }

    this.outputCanvas.drawImage(this.baseLayerCanvas, 0, 0).save();

    this.applyTextEffects();

    this.outputCanvas.restore();

    let cnv = this.outputCanvas;

    if (this.perspective) {
      cnv = this.applyPerspective(this.outputCanvas);
    }

    this.ctx
      .save()
      .rotateAroundPoint(
        this.radianAngle(-this.angle),
        this.width / 2,
        this.height / 2
      )
      .translate(
        -cnv.width / 2 + this.width / 2,
        -cnv.height / 2 + this.height / 2
      )
      .drawImage(cnv, 0, 0)
      .restore();

    this.shrinkInternalCanvases();
  }

  /**
   * Draws the plain text to the canvas with no effects applied
   *
   * @param {HTMLCanvasElement} canvas The canvas to draw the text to
   * @param {Boolean} fill whether or not to fill the text
   * @param {Boolean} stroke whether or not to stroke the text
   * @param {String} color an optional CSS compatible colour string to override this.color
   */
  drawBaseText(canvas, fill = true, stroke = false, color = null) {
    canvas.save();

    this.activeDrawingCanvas = canvas;
    const originalFontSize = this.fontSize;
    const originalLetterSpacing = this.letterSpacing;
    this.activeDrawingCanvas.save();

    if (this.enforceMaxWidth && this.width > this.maxWidth * this.canvasScale) {
      this.fontSize *= (this.maxWidth * this.canvasScale) / this.width;
      this.letterSpacing *= (this.maxWidth * this.canvasScale) / this.width;

      switch (this.textAlign) {
        case 'right':
          this.activeDrawingCanvas.translate(
            this.width - this.maxWidth * this.canvasScale,
            (originalFontSize - this.fontSize) / 2
          );
          break;
        case 'left':
          this.activeDrawingCanvas.translate(
            0,
            (originalFontSize - this.fontSize) / 2
          );
          break;
        case 'center':
        default:
          this.activeDrawingCanvas.translate(
            (this.width - this.maxWidth * this.canvasScale) / 2,
            (originalFontSize - this.fontSize) / 2
          );
          break;
      }
    }

    this.setFont();
    if (this.blockMode) {
      this.drawBackgroundBlock(color);
    }

    this.activeDrawingCanvas.translate(
      0,
      this.fontSize * this.canvasScale * (5 / 6)
    );

    this.lines.forEach((line, i) => {
      if (line) {
        canvas.save();
        this.applyLineAlignmentOffset(line);
        this.drawTextLine(line, i, fill, stroke, color);
        canvas.restore();
      }
    });
    canvas.restore();

    this.fontSize = originalFontSize;
    this.letterSpacing = originalLetterSpacing;
    this.activeDrawingCanvas.restore();
  }

  /**
   * Apply all font options to the current active drawing canvas
   */
  setFont() {
    const scale = this.canvasScale;
    const fontSize = `${this.fontSize * scale}px`;
    this.activeDrawingCanvas.font = [
      this.fontStyle,
      this.fontWeight,
      fontSize,
      this.font,
    ]
      .filter(Boolean)
      .join(' ');
  }

  drawBackgroundBlock(color) {
    this.activeDrawingCanvas
      .setFillStyle(color || this.color)
      .fillRect(
        this.activeDrawingCanvas.width / 2 - this.width / 2,
        this.activeDrawingCanvas.height / 2 - this.height / 2,
        this.width,
        this.height
      );
  }

  /**
   * translate the active canvas to horizontally align the text based on the active text
   * alignment (centred, left, right)
   */
  applyLineAlignmentOffset(line) {
    const scale = this.canvasScale;
    const offset = this.getLineAlignmentOffset(line) * scale;
    this.activeDrawingCanvas.translate(offset, 0);
  }

  getLineAlignmentOffset(line, longestLineWidth = null) {
    if (this.textAlign === 'left') {
      return 0;
    }

    const longestWidth =
      typeof longestLineWidth === 'number'
        ? longestLineWidth
        : this.measureText(this.getLongestLine());
    const thisLineWidth = line ? this.measureText(line) : 0;
    const delta = longestWidth - thisLineWidth;

    if (this.textAlign === 'right') {
      return delta;
    }

    if (this.textAlign === 'center') {
      return delta / 2;
    }

    return 0;
  }

  /**
   * Draws a single line of text to the canvas
   *
   * @param {string} line The line to draw
   * @param {number} lineIndex The index of the line in the array of lines (e.g. the first line would be 0)
   * @param {boolean} fill Whether or not to fill the text
   * @param {boolean} stroke Whether or not to stroke the text
   * @param {string} color An optional CSS compatible string colour to override this.color
   */
  drawTextLine(line, lineIndex, fill = true, stroke = false, color = null) {
    this.activeDrawingCanvas.save();
    const scale = this.canvasScale;
    const lineHeight = this.fontSize * scale * this.lineHeight;

    this.activeDrawingCanvas.translate(0, lineHeight * lineIndex);

    const characters = this.getLineCharacters(line);

    if (this.textDirection === 'rtl' && !this.letterSpacing) {
      // Arabic letters have a different appearance depending on their position in the word
      // so rather than drawing characters one by one, we need to draw the whole word at once
      // but this is only possible when letterSpacing is set to 0
      this.drawTextCharacter(
        characters.reverse().join(''),
        fill,
        stroke,
        color
      );
    } else {
      characters.forEach((character, characterIndex) => {
        if (this.gradient && !this.interpolateGradient) {
          this.applyBlockGradientToCharacter(line, characterIndex, lineIndex);
        }
        if (this.curvature !== 0) {
          const realWidth = this.getTextWidth(false) * this.canvasScale;
          const lineWidth = this.measureText(line) * this.canvasScale;
          const widthScale = realWidth / this.width;
          const isMaxWidthOverflow =
            this.enforceMaxWidth &&
            this.maxWidth &&
            this.width > this.maxWidth * this.canvasScale;
          const arcReferenceWidth = isMaxWidthOverflow
            ? this.maxWidth * this.canvasScale
            : this.width;
          const radius = isMaxWidthOverflow
            ? (this.curvature * this.relativeFontScale * this.canvasScale) /
              (realWidth / arcReferenceWidth)
            : (this.curvature * this.relativeFontScale * this.canvasScale) /
              (realWidth / this.width);
          const circumference = 2 * Math.PI * radius;
          const arcLength = isMaxWidthOverflow
            ? (lineWidth / circumference) * Math.PI * 2
            : ((lineWidth * widthScale) / circumference) * Math.PI * 2;
          const fullArcLength = isMaxWidthOverflow
            ? (arcReferenceWidth / circumference) * Math.PI * 2
            : ((this.width * widthScale) / circumference) * Math.PI * 2;
          const lineSlice =
            this.measureText(line.slice(0, characterIndex + 1)) -
            this.measureText(character) / 2;
          const lineProgress = lineWidth > 0 ? lineSlice / lineWidth : 0;
          let charArc = arcLength * lineProgress * this.canvasScale;

          if (this.textAlign === 'left') {
            charArc -= (fullArcLength - arcLength) / 2;
          } else if (this.textAlign === 'right') {
            charArc += (fullArcLength - arcLength) / 2;
          }
          const angleOffset = -arcLength / 2 + charArc;
          const layoutWidth = isMaxWidthOverflow ? arcReferenceWidth : realWidth;
          this.activeDrawingCanvas
            .save()
            .translate(-(this.width - layoutWidth) / 2, 0)
            .translate(0, -this.height / 2)
            .when(
              this.textAlign === 'center' && !isMaxWidthOverflow,
              ctx => ctx.translate(-(this.width - lineWidth) / 2, 0)
            )
            .when(
              this.textAlign === 'right' && !isMaxWidthOverflow,
              ctx => ctx.translate(-(this.width - lineWidth), 0)
            )
            .rotateAroundPoint(
              angleOffset,
              this.activeDrawingCanvas.width / 2,
              this.activeDrawingCanvas.height / 2 +
                radius -
                (this.fontSize / 2) * this.canvasScale
            )
            .translate(
              this.width / 2 -
                ((this.measureText(character) + this.letterSpacing) / 2) *
                  this.canvasScale,
              this.height / 2
            );
        }
        this.drawTextCharacter(character, fill, stroke, color);
        if (this.curvature !== 0) {
          this.activeDrawingCanvas.restore();
        }
      });
    }
    this.activeDrawingCanvas.restore();
  }

  /**
   * Converts a line of text into an array of individual characters.
   * This is required because an emoji character is technically 2 or more characters,
   * but this method will treat them as 1
   *
   * @param {string} line The line to retrieve characters from
   * @returns an array of strings representing each individual character
   */
  getLineCharacters(line) {
    let letters = [];

    if (!stringContainsEmojis(line)) {
      letters = line.split('');
    } else if (!this.options.enableEmojis) {
      letters = removeEmojis(line).split('');
    } else {
      // if the string contains emojis we need to break the string apart into
      // an array of characters, otherwise the emojis will be broken down into
      // their incomprehensible parts
      const stringParts = splitStringByEmojis(line);
      stringParts.forEach(part => {
        if (stringContainsEmojis(part)) {
          letters.push(part);
        } else {
          const stringPartLetters = part.split('');

          stringPartLetters.forEach(letter => letters.push(letter));
        }
      });
    }

    if (this.textDirection === 'rtl') {
      return letters.reverse();
    }

    return letters;
  }

  /**
   * Draws a single character to the canvas
   *
   * @param {string} character A single character to draw
   * @param {boolean} fill Whether or not to fill the text
   * @param {boolean} stroke Whether or not to stroke the text
   * @param {string} color An optional CSS compatible string colour to override this.color
   */
  drawTextCharacter(character, fill = true, stroke = false, color = null) {
    const scale = this.canvasScale;

    const xOffset = this.activeDrawingCanvas.width / 2 - this.width / 2;
    const yOffset = this.activeDrawingCanvas.height / 2 - this.height / 2;

    this.activeDrawingCanvas
      .when(this.blockMode, ctx => ctx.setCompositeOperation('destination-out'))
      .when(
        // Firefox doesn't render text above 2,000 pixels. We need to scale the
        // canvas up accordingly to compensate
        navigator.userAgent.indexOf('Firefox') > -1 &&
          this.fontSize * this.canvasScale > 2000,
        ctx =>
          ctx
            .save()
            .scaleFromPosition(
              (this.fontSize * this.canvasScale) / 2000,
              (this.fontSize * this.canvasScale) / 2000,
              xOffset,
              yOffset
            )
      )
      .when(stroke, ctx =>
        ctx
          .save()
          .setStrokeStyle(color || this.strokeColor || this.color)
          .setLineWidth(
            this.strokeWidth * this.relativeFontScale * this.canvasScale
          )
          .setCompositeOperation('destination-over')
          .strokeText(character, xOffset, yOffset)
          .restore()
      )
      .when(fill, ctx => {
        if (!this.gradient) {
          ctx.setFillStyle(color || this.color);
        }

        ctx.fillText(character, xOffset, yOffset);
      })
      .when(
        navigator.userAgent.indexOf('Firefox') > -1 &&
          this.fontSize * this.canvasScale > 2000,
        ctx => ctx.restore()
      );

    this.activeDrawingCanvas.translate(
      (this.measureText(character) + this.letterSpacing) * scale,
      0
    );
  }

  /**
   * When this.interpolateGradient is false, the gradient is rendered as a block,
   * with each character or each line being a different colour
   *
   * @param {string} line The line the character belongs to
   * @param {number} characterIndex An index representing the position in the string
   * @param {number} lineIndex The line's index in the array of lines
   */
  applyBlockGradientToCharacter(line, characterIndex, lineIndex) {
    const lineProgress =
      this.gradientDirection === 'X'
        ? characterIndex / line.length
        : lineIndex / this.lines.length;
    const closestStop = this.gradient.closestStopToPosition(lineProgress);
    const color = closestStop?.color ? rgbToHex(closestStop.color) : '#000';

    this.activeDrawingCanvas.setFillStyle(color);
  }

  /**
   * Fills the base layer canvas with a gradient
   */
  applyGradientFill() {
    if (this.gradient.stops && this.interpolateGradient) {
      this.baseLayerCanvas
        .save()
        .setFillStyle(this.createGradientFill())
        .setCompositeOperation('source-atop')
        .fillRect(0, 0, this.baseLayerCanvas.width, this.baseLayerCanvas.height)
        .restore();
    }
  }

  /**
   * Creates a CanvasGradient from the current gradient options
   *
   * @returns CanvasGradient
   */
  createGradientFill() {
    const scale = this.canvasScale;
    const lineHeight = this.fontSize * scale * this.lineHeight;
    const longestLine = this.getLongestLine();
    const longestLineWidth = this.measureText(longestLine);

    const horizontal = this.gradientDirection === 'X';
    const vertical = this.gradientDirection === 'Y';

    const x1 = this.baseLayerCanvas.width / 2 - this.width / 2;
    const y1 = this.baseLayerCanvas.height / 2 - this.height / 2;

    const gradient = this.baseLayerCanvas.createLinearGradient(
      horizontal ? x1 : 0,
      vertical ? y1 : 0,
      horizontal ? x1 + longestLineWidth * scale : 0,
      vertical ? y1 + lineHeight * this.lines.length : 0
    );

    this.gradient.toCanvasGradient(gradient, this.interpolateGradient);

    return gradient;
  }

  applyTextEffects() {
    this.applyBlur();
    this.applyExtrusion();
    this.applyShadow();
    this.applyDistressedEffect();
  }

  /**
   * Applies the 'extrusion' effect, creating clones of the base text
   * in different colours and offset by a translation vector
   */
  applyExtrusion() {
    this.outputCanvas.save();

    if (this.extrusion) {
      const extrudeDirection = this.extrusionDirection;

      this.temporaryCanvas.clear().drawImage(this.baseLayerCanvas, 0, 0).save();

      const layersPerColor = Math.max(
        1,
        Math.floor(
          (this.extrusion / this.extrusionColors.length) *
            this.relativeFontScale
        )
      );

      this.extrusionColors.forEach(color => {
        this.temporaryCanvas
          .setCompositeOperation('source-in')
          .colourFill(color);

        for (let i = 0; i < layersPerColor; ++i) {
          this.outputCanvas
            .setCompositeOperation('destination-over')
            .translate(
              extrudeDirection.x * this.canvasScale,
              extrudeDirection.y * this.canvasScale
            )
            .drawImage(this.temporaryCanvas);
        }
      });

      this.temporaryCanvas.restore();
    }

    this.outputCanvas.restore();
  }

  /**
   * Applies the 'blur' effect, creating clones of the base text
   * using stroke effect only, with reduced opacity and offset
   * by a translation vector
   */
  applyBlur() {
    if (!this.directionalBlur?.x) {
      return;
    }
    this.outputCanvas.save();
    this.strokeWidth *= 0.125;

    const scale = this.canvasScale;

    this.temporaryCanvas.clear();

    const distance = this.directionalBlur.magnitude();
    const direction = this.directionalBlur.normalize();

    const cnv = new SierraCanvas();
    cnv.resizeToImage(this.temporaryCanvas);

    this.drawBaseText(cnv, false, true, this.color);

    const layerCount = distance * scale * this.relativeFontScale;
    for (let i = 0; i < layerCount; ++i) {
      this.temporaryCanvas
        .save()
        .setGlobalAlpha(0.75 - (i / layerCount) * 0.75)
        .translate(direction.x * i, direction.y * i)
        .drawImage(cnv);

      this.temporaryCanvas.restore();
    }

    cnv.destroy();

    this.outputCanvas
      .setCompositeOperation('destination-over')
      .drawImage(this.temporaryCanvas, 0, 0);

    this.strokeWidth *= 8;

    this.outputCanvas.restore();
  }

  /**
   * Applies a shadow to the base omnicanvas.renderer canvas
   */
  applyShadow() {
    if (this.shadow?.color && this.shadow?.offset) {
      this.ctx.shadowColor = this.shadow.color;
      this.ctx.shadowBlur =
        this.shadow.blur * this.canvasScale * this.relativeFontScale;
      this.ctx.shadowOffsetX =
        this.shadow.offset.x * this.canvasScale * this.relativeFontScale;
      this.ctx.shadowOffsetY =
        this.shadow.offset.y * this.canvasScale * this.relativeFontScale;
    }
  }

  /**
   * Applies a distressed effect by overlaying a transparency mask
   */
  applyDistressedEffect() {
    if (!this.distressed) {
      return;
    }

    if (!this.distressImage) {
      const image = new Image();
      image.crossOrigin = 'Anonymous';
      image.onload = () => {
        this.distressImage = image;
        this.controller?.render?.();
      };
      image.src = distressCloudImage;
    } else {
      const padding = this.getBoundsPadding();
      const paddingLeft = padding.left ?? 0;
      const paddingTop = padding.top ?? 0;
      const paddedWidth = this.width + paddingLeft + (padding.right ?? 0);
      const paddedHeight = this.height + paddingTop + (padding.bottom ?? 0);

      this.outputCanvas
        .save()
        .setCompositeOperation('destination-in')
        .drawImage(
          this.distressImage,
          this.outputCanvas.width / 2 - this.width / 2 - paddingLeft,
          this.outputCanvas.height / 2 - this.height / 2 - paddingTop,
          paddedWidth,
          paddedHeight
        )
        .restore();
    }
  }

  /**
   * Draws an animated cursor at the position of the text you are currently
   * editing
   *
   * @return {void}
   */
  drawCursor() {
    if (
      this.highlight.length === 2 &&
      this.highlight[0] !== this.highlight[1] &&
      !isNaN(this.highlight[0]) &&
      !isNaN(this.highlight[1])
    ) {
      return this.drawHighlight();
    }

    let activeLine = this.getActiveLine();
    let cursorPositionX = 0;
    let cursorPositionY = 0;
    const text = this.lines.join('\n');
    const cursorPosition = this.hiddenTextEditor.selectionStart;
    const outputLines = this.lines;
    let outputLine = outputLines[this.activeLineIndex];
    const canvasScale = this.canvasScale;

    Object.keys(text).forEach(index => {
      if (index >= cursorPosition) {
        return;
      }
      const character = text[index];

      if (character === '\n') {
        cursorPositionX = 0;
        cursorPositionY++;
      } else {
        cursorPositionX++;
      }
    });

    activeLine = this.lines[cursorPositionY] || '';
    outputLine = outputLines[cursorPositionY];
    this.ctx.save();
    this.ctx.font = `${
      (this.fontWeight ? `${this.fontWeight} ` : null) + this.fontSize
    }px ${this.font}`;
    const lineWidth = this.measureText(outputLine);
    this.ctx.font = `${
      (this.fontWeight ? `${this.fontWeight} ` : null) + this.fontSize
    }px ${this.font}`;

    this.ctx.beginPath();

    const boundsWidth = this.controller.boundingBox.width;
    const characterXPosition = this.measureText(
      activeLine.slice(0, cursorPositionX)
    );

    let positionOffsetX = 0;

    if (this.textAlign === 'center') {
      positionOffsetX = this.width / 2 - lineWidth / 2;
    } else if (this.textAlign === 'right') {
      positionOffsetX = this.width - lineWidth;
    }

    this.ctx.rect(
      (characterXPosition - boundsWidth / 2) * canvasScale +
        (boundsWidth * canvasScale) / 2 +
        positionOffsetX,
      (1 / canvasScale + this.fontSize * this.lineHeight * cursorPositionY) *
        canvasScale,
      4,
      this.fontSize * canvasScale
    );

    this.ctx.globalAlpha = 1 * Math.round(this.cursorOpacity);
    this.ctx.fillStyle = '#fff';
    this.ctx.strokeStyle = '#000';
    this.ctx.fill();
    this.ctx.globalAlpha = 0.5 * Math.round(this.cursorOpacity);
    this.ctx.lineWidth = 2;
    this.ctx.stroke();
    this.ctx.restore();
    return true;
  }

  /**
   * Draw a highlight around the corresponding text matching that which is
   * highlighted in the hidden textarea
   *
   * @return {void}
   */
  drawHighlight() {
    const highlightPoints = this.highlight.slice(0, 2);
    const text = this.lines.join('\n');
    const outputLines = this.lines;
    let outputLine = outputLines[this.activeLineIndex];
    const canvasScale = this.canvasScale;
    highlightPoints.sort((a, b) => a - b);
    const l = highlightPoints[1];
    this.ctx.save();
    this.ctx.globalAlpha = 0.5;
    this.ctx.fillStyle = this.highlightHex;
    this.ctx.font = `${
      (this.fontWeight ? `${this.fontWeight} ` : null) + this.fontSize
    }px ${this.font}`;

    for (let i = highlightPoints[0]; i < l; ++i) {
      this.ctx.font = `${
        (this.fontWeight ? `${this.fontWeight} ` : null) + this.fontSize
      }px ${this.font}`;
      if (text[i] !== '\n') {
        const cursorPosition = i;
        let cursorPositionX = 0;
        let cursorPositionY = 0;
        const characters = Object.keys(text);
        for (let c = 0; c < characters.length; ++c) {
          if (c < cursorPosition) {
            const character = text[c];
            if (character === '\n') {
              cursorPositionX = 0;
              cursorPositionY++;
            } else {
              cursorPositionX++;
            }
          }
        }
        const activeLine = this.lines[cursorPositionY] || '';
        outputLine = outputLines[cursorPositionY];

        const lineWidth = this.measureText(outputLine) * canvasScale;

        const outputLineToActiveLineRatio =
          lineWidth / this.measureText(activeLine);
        this.ctx.font = `${
          (this.fontWeight ? `${this.fontWeight} ` : null) + this.fontSize
        }px ${this.font}`;

        this.ctx.beginPath();

        const boundsWidth = this.controller.boundingBox.width;

        const highlightXPosition = this.measureText(
          activeLine.slice(0, cursorPositionX)
        );
        const highlightWidth =
          this.measureText(text[i]) * outputLineToActiveLineRatio;

        let positionOffsetX = 0;

        if (this.textAlign === 'center') {
          positionOffsetX = this.width / 2 - lineWidth / 2;
        } else if (this.textAlign === 'right') {
          positionOffsetX = this.width - lineWidth;
        }

        this.ctx.rect(
          (highlightXPosition - boundsWidth / 2) * canvasScale +
            (boundsWidth * canvasScale) / 2 +
            positionOffsetX,
          (1 / canvasScale +
            this.fontSize * this.lineHeight * cursorPositionY) *
            canvasScale,
          highlightWidth,
          this.fontSize * canvasScale
        );

        this.ctx.fill();
      }
    }
    this.ctx.restore();
  }

  /**
   * Occurs before drawing the element to the canvas
   *
   * @return {void}
   */
  beforeRender() {
    this.getRealDimensions();
    this.ctx.save();
  }

  beforeRenderBounds() {
    const padding = this.getBoundsPadding();
    const scale = this.scale * this.canvasScale;
    const paddingLeft = padding.left * scale;
    const paddingRight = padding.right * scale;
    const paddingTop = padding.top * scale;
    const paddingBottom = padding.bottom * scale;

    if (!paddingLeft && !paddingRight && !paddingTop && !paddingBottom) {
      return;
    }

    this.width = Math.max(0, this.width + paddingLeft + paddingRight);
    this.height = Math.max(0, this.height + paddingTop + paddingBottom);

    this.ctx.translate(-paddingLeft, -paddingTop);

    this.outerBounds = this.calculateOuterBounds();
  }

  afterRender() {
    this.ctx.restore();

    if (this.controller?.params?.exportMode) {
      this.perspectiveCanvas?.destroy();
      this.perspectiveCanvas = null;
    }
  }

  /**
   * The width and height of this element behave differently to other elements
   * so we need to calculate the width and height whenever the text is changed
   *
   * @return {void}
   */
  getRealDimensions() {
    this.width = this.getTextWidth(false);
    this.height = this.getTextHeight(false);
  }

  getBoundsPadding() {
    const effectPadding = this.getTextEffectPadding();
    const inkInsets = this.getTextInkInsets();
    const overflowInsets = inkInsets.hasMeasuredMetrics
      ? {left: 0, right: 0, top: 0, bottom: 0}
      : this.getFontOverflowInsets();
    const curvaturePadding = this.getCurvatureHeightPadding(
      this.getTextWidth(false),
      effectPadding.x
    );
    const curvatureXPadding = this.getCurvatureWidthPadding(
      this.getTextWidth(false),
      effectPadding.x
    );
    const curvatureXMultiplier = this.curvature > 0 ? -1 : 1;
    const curvatureXOffset = curvatureXPadding * curvatureXMultiplier;
    const effectPaddingLeft =
      typeof effectPadding.left === 'number'
        ? effectPadding.left
        : effectPadding.x ?? 0;
    const effectPaddingRight =
      typeof effectPadding.right === 'number'
        ? effectPadding.right
        : effectPadding.x ?? 0;
    const effectPaddingTop =
      typeof effectPadding.top === 'number'
        ? effectPadding.top
        : effectPadding.y ?? 0;
    const effectPaddingBottom =
      typeof effectPadding.bottom === 'number'
        ? effectPadding.bottom
        : effectPadding.y ?? 0;
    const basePaddingLeft =
      inkInsets.left + effectPaddingLeft + overflowInsets.left;
    const basePaddingRight =
      inkInsets.right + effectPaddingRight + overflowInsets.right;
    const maxShrink = Math.max(0, Math.min(basePaddingLeft, basePaddingRight));
    const clampedCurvatureOffset = Math.max(curvatureXOffset, -maxShrink);

    let paddingTop = inkInsets.top + effectPaddingTop + overflowInsets.top;
    let paddingBottom =
      inkInsets.bottom + effectPaddingBottom + overflowInsets.bottom;

    if (this.curvature > 0) {
      paddingBottom += curvaturePadding;
    } else if (this.curvature < 0) {
      paddingTop += curvaturePadding;
    }

    return {
      left: basePaddingLeft + clampedCurvatureOffset,
      right: basePaddingRight + clampedCurvatureOffset,
      top: paddingTop,
      bottom: paddingBottom,
    };
  }

  getInteractionBounds() {
    const padding = this.getBoundsPadding();
    const width = this.width + padding.left + padding.right;
    const height = this.height + padding.top + padding.bottom;

    return {
      width: Math.max(0, width),
      height: Math.max(0, height),
      offsetX: (padding.right - padding.left) / 2,
      offsetY: (padding.bottom - padding.top) / 2,
    };
  }

  calculateOuterBounds() {
    const padding = this.getBoundsPadding();
    const width = Math.max(
      0,
      (this.width + padding.left + padding.right) * this.canvasScale
    );
    const height = Math.max(
      0,
      (this.height + padding.top + padding.bottom) * this.canvasScale
    );
    const leftOffset = padding.left * this.canvasScale;
    const topOffset = padding.top * this.canvasScale;
    const centerPoint = new Vector(
      width / 2 - leftOffset,
      height / 2 - topOffset
    );
    const tl = new Vector(-leftOffset, -topOffset).rotateAround(
      centerPoint.x,
      centerPoint.y,
      -this.angle
    );
    const tr = new Vector(width - leftOffset, -topOffset).rotateAround(
      centerPoint.x,
      centerPoint.y,
      -this.angle
    );
    const bl = new Vector(-leftOffset, height - topOffset).rotateAround(
      centerPoint.x,
      centerPoint.y,
      -this.angle
    );
    const br = new Vector(width - leftOffset, height - topOffset).rotateAround(
      centerPoint.x,
      centerPoint.y,
      -this.angle
    );

    const minX = Math.min(tl.x, tr.x, bl.x, br.x);
    const maxX = Math.max(tl.x, tr.x, bl.x, br.x);
    const minY = Math.min(tl.y, tr.y, bl.y, br.y);
    const maxY = Math.max(tl.y, tr.y, bl.y, br.y);

    return {
      tl: new Vector(minX, minY),
      tr: new Vector(maxX, minY),
      bl: new Vector(minX, maxY),
      br: new Vector(maxX, maxY),
    };
  }

  getLongestLineIndex() {
    let max = 0;
    let lineWidth = 0;
    let longestLineIndex = 0;
    const lines = this.lines;

    lines.forEach((line, index) => {
      lineWidth = this.measureText(line);
      if (lineWidth > max) {
        max = lineWidth;
        longestLineIndex = index;
      }
    });

    return longestLineIndex;
  }

  getLongestLine() {
    return this.lines[this.getLongestLineIndex()];
  }

  /**
   * Calculate the width of the text in pixels, where the width is the width of
   * the longest line
   *
   * @param {Boolean} includeEchoes "echoes" in this case refers to anything that appears beyond the
   * base text, such as shadows, extrusions, blur etc.
   *
   * @return {Number}
   */
  getTextWidth(includeEchoes = true) {
    let max = 0;
    let lineWidth = 0;
    const lines = this.lines;

    lines.forEach(line => {
      lineWidth = this.measureText(line);
      if (lineWidth > max) {
        max = lineWidth;
      }
    });

    if (!includeEchoes) {
      return max;
    }

    const effectPadding = this.getTextEffectPadding();
    const paddingLeft =
      typeof effectPadding.left === 'number'
        ? effectPadding.left
        : effectPadding.x ?? 0;
    const paddingRight =
      typeof effectPadding.right === 'number'
        ? effectPadding.right
        : effectPadding.x ?? 0;

    return max + paddingLeft + paddingRight;
  }

  /**
   * Calculate the width of the text in pixels, where the height is the fontsize
   * multiplied by line height multiplied by the number of lines
   *
   * @param {Boolean} includeEchoes "echoes" in this case refers to anything that appears beyond the
   * base text, such as shadows, extrusions, blur etc.
   *
   * @return {Number}
   */
  getTextHeight(includeEchoes = true) {
    const lineCount = Math.max(1, this.lines.length);
    const lineGap = this.fontSize * this.lineHeight;
    let height = this.fontSize + Math.max(0, lineCount - 1) * lineGap;

    if (!includeEchoes) {
      return height;
    }

    const effectPadding = this.getTextEffectPadding();
    const paddingTop =
      typeof effectPadding.top === 'number'
        ? effectPadding.top
        : effectPadding.y ?? 0;
    const paddingBottom =
      typeof effectPadding.bottom === 'number'
        ? effectPadding.bottom
        : effectPadding.y ?? 0;

    height += paddingTop + paddingBottom;

    return height;
  }

  getTextInkBounds() {
    const lineCount = Math.max(1, this.lines.length);
    const longestLineWidth = this.getTextWidth(false);
    const lineGap = this.fontSize * this.lineHeight;
    const baselineStart = this.fontSize * (5 / 6);
    const fallbackAscent = this.fontSize * 0.8;
    const fallbackDescent = this.fontSize * 0.2;
    let left = Infinity;
    let right = -Infinity;
    let top = Infinity;
    let bottom = -Infinity;
    let hasMeasuredMetrics = false;

    for (let lineIndex = 0; lineIndex < lineCount; ++lineIndex) {
      const line = this.lines[lineIndex] ?? '';
      const baselineY = baselineStart + lineGap * lineIndex;
      const lineOffsetX = this.getLineAlignmentOffset(line, longestLineWidth);

      if (!line) {
        top = Math.min(top, baselineY - fallbackAscent);
        bottom = Math.max(bottom, baselineY + fallbackDescent);
        continue;
      }

      const metrics = this.measureText(line, true);
      const lineWidth = this.measureText(line);
      const hasActualMetrics =
        typeof metrics.actualBoundingBoxLeft === 'number' &&
        typeof metrics.actualBoundingBoxRight === 'number' &&
        typeof metrics.actualBoundingBoxAscent === 'number' &&
        typeof metrics.actualBoundingBoxDescent === 'number';

      let lineLeft = lineOffsetX;
      let lineRight = lineOffsetX + lineWidth;
      let lineTop = baselineY - fallbackAscent;
      let lineBottom = baselineY + fallbackDescent;

      if (hasActualMetrics) {
        hasMeasuredMetrics = true;
        const nativeWidth =
          typeof metrics.width === 'number' ? metrics.width : lineWidth;
        const spacingDelta = lineWidth - nativeWidth;
        lineLeft = lineOffsetX - (metrics.actualBoundingBoxLeft || 0);
        lineRight =
          lineOffsetX + (metrics.actualBoundingBoxRight || 0) + spacingDelta;
        lineTop = baselineY - (metrics.actualBoundingBoxAscent || fallbackAscent);
        lineBottom =
          baselineY + (metrics.actualBoundingBoxDescent || fallbackDescent);
      }

      left = Math.min(left, lineLeft);
      right = Math.max(right, lineRight);
      top = Math.min(top, lineTop);
      bottom = Math.max(bottom, lineBottom);
    }

    if (
      !Number.isFinite(left) ||
      !Number.isFinite(right) ||
      !Number.isFinite(top) ||
      !Number.isFinite(bottom)
    ) {
      return {
        left: 0,
        right: this.getTextWidth(false),
        top: 0,
        bottom: this.getTextHeight(false),
        hasMeasuredMetrics: false,
      };
    }

    return {
      left,
      right,
      top,
      bottom,
      hasMeasuredMetrics,
    };
  }

  getTextInkInsets() {
    const width = this.getTextWidth(false);
    const height = this.getTextHeight(false);
    const inkBounds = this.getTextInkBounds();

    return {
      left: -inkBounds.left,
      right: inkBounds.right - width,
      top: -inkBounds.top,
      bottom: inkBounds.bottom - height,
      hasMeasuredMetrics: inkBounds.hasMeasuredMetrics,
    };
  }

  /**
   * Calculate additional padding required to fit text effects such as strokes,
   * shadows, extrusion and directional blur.
   *
   * @returns {{x: number, y: number}}
   */
  getTextEffectPadding() {
    const padding = {
      x: 0,
      y: 0,
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
    };

    if (this.stroke && this.strokeWidth) {
      const strokePadding = (this.strokeWidth * this.relativeFontScale) / 2;
      padding.left += strokePadding;
      padding.right += strokePadding;
      padding.top += strokePadding;
      padding.bottom += strokePadding;
    }

    if (this.extrusion && this.extrusionDirection) {
      const extrusionX =
        (this.extrusionDirection.x || 0) *
        this.extrusion *
        this.relativeFontScale;
      const extrusionY =
        (this.extrusionDirection.y || 0) *
        this.extrusion *
        this.relativeFontScale;

      if (extrusionX > 0) {
        padding.right += extrusionX;
      } else if (extrusionX < 0) {
        padding.left += Math.abs(extrusionX);
      }

      if (extrusionY > 0) {
        padding.bottom += extrusionY;
      } else if (extrusionY < 0) {
        padding.top += Math.abs(extrusionY);
      }
    }

    if (this.directionalBlur) {
      const blurX = (this.directionalBlur.x || 0) * this.relativeFontScale;
      const blurY = (this.directionalBlur.y || 0) * this.relativeFontScale;

      if (blurX > 0) {
        padding.right += blurX;
      } else if (blurX < 0) {
        padding.left += Math.abs(blurX);
      }

      if (blurY > 0) {
        padding.bottom += blurY;
      } else if (blurY < 0) {
        padding.top += Math.abs(blurY);
      }
    }

    if (this.shadow?.color && this.shadow?.offset) {
      const blur = (this.shadow.blur || 0) * this.relativeFontScale;
      const shadowX = (this.shadow.offset.x || 0) * this.relativeFontScale;
      const shadowY = (this.shadow.offset.y || 0) * this.relativeFontScale;

      padding.left += Math.max(0, -shadowX) + blur;
      padding.right += Math.max(0, shadowX) + blur;
      padding.top += Math.max(0, -shadowY) + blur;
      padding.bottom += Math.max(0, shadowY) + blur;
    }

    padding.x = Math.max(padding.left, padding.right);
    padding.y = Math.max(padding.top, padding.bottom);

    return padding;
  }

  /**
   * Calculate extra padding needed for fonts whose glyphs overflow their
   * nominal bounding boxes (e.g. decorative scripts).
   *
   * @returns {{x: number, y: number}}
   */
  getFontOverflowInsets() {
    const padding = {left: 0, right: 0, top: 0, bottom: 0};

    if (!this.lines?.length) {
      return padding;
    }

    const canvas = SierraCanvas.global;
    canvas.font = [
      this.fontStyle,
      this.fontWeight,
      `${this.fontSize}px`,
      this.font,
    ]
      .filter(Boolean)
      .join(' ');

    this.lines.forEach(line => {
      if (!line) {
        return;
      }

      const metrics = canvas.measureText(line);

      if (
        typeof metrics.actualBoundingBoxLeft !== 'number' ||
        typeof metrics.actualBoundingBoxRight !== 'number' ||
        typeof metrics.actualBoundingBoxAscent !== 'number' ||
        typeof metrics.actualBoundingBoxDescent !== 'number'
      ) {
        return;
      }

      const lineWidth =
        typeof metrics.width === 'number'
          ? metrics.width
          : this.measureText(line);
      const overflowLeft = Math.max(0, metrics.actualBoundingBoxLeft || 0);
      const overflowRight = Math.max(
        0,
        (metrics.actualBoundingBoxRight || 0) - lineWidth
      );
      padding.left = Math.max(padding.left, overflowLeft);
      padding.right = Math.max(padding.right, overflowRight);
      const ascent = metrics.actualBoundingBoxAscent || 0;
      const descent = metrics.actualBoundingBoxDescent || 0;
      const overflowTop = Math.max(0, ascent - this.fontSize * 0.8);
      const overflowBottom = Math.max(0, descent - this.fontSize * 0.2);

      padding.top = Math.max(padding.top, overflowTop);
      padding.bottom = Math.max(padding.bottom, overflowBottom);
    });

    return padding;
  }

  /**
   * Estimate vertical padding required to contain curved text.
   *
   * @param {number} baseWidth The width of the text without effects applied
   * @param {number} effectPaddingX Horizontal padding from effects
   * @returns {number}
   */
  getCurvatureHeightPadding(baseWidth, effectPaddingX = 0) {
    if (!this.curvature || !baseWidth) {
      return 0;
    }

    const totalWidth = baseWidth + effectPaddingX * 2;
    const widthScale = totalWidth ? baseWidth / totalWidth : 1;
    const radius =
      (this.curvature * this.relativeFontScale * totalWidth) / baseWidth;
    const radiusAbs = Math.abs(radius);

    if (!radiusAbs) {
      return 0;
    }

    let maxSagitta = 0;

    this.lines.forEach(line => {
      const lineWidth = this.measureText(line);
      if (!lineWidth) {
        return;
      }

      const arcAngle = (lineWidth * widthScale) / radiusAbs;
      const sagitta = radiusAbs - Math.cos(arcAngle / 2) * radiusAbs;

      if (!Number.isNaN(sagitta)) {
        maxSagitta = Math.max(maxSagitta, sagitta);
      }
    });

    return maxSagitta;
  }

  /**
   * Estimate horizontal padding required to contain rotated glyphs on curved text.
   *
   * @param {number} baseWidth The width of the text without effects applied
   * @param {number} effectPaddingX Horizontal padding from effects
   * @returns {number}
   */
  getCurvatureWidthPadding(baseWidth, effectPaddingX = 0) {
    if (!this.curvature || !baseWidth) {
      return 0;
    }

    const totalWidth = baseWidth + effectPaddingX * 2;
    const widthScale = totalWidth ? baseWidth / totalWidth : 1;
    const radius =
      (this.curvature * this.relativeFontScale * totalWidth) / baseWidth;
    const radiusAbs = Math.abs(radius);

    if (!radiusAbs) {
      return 0;
    }

    let maxHalfAngle = 0;

    this.lines.forEach(line => {
      const lineWidth = this.measureText(line);
      if (!lineWidth) {
        return;
      }

      const arcAngle = (lineWidth * widthScale) / radiusAbs;
      maxHalfAngle = Math.max(maxHalfAngle, Math.abs(arcAngle) / 2);
    });

    const glyphHalfSize = (this.fontSize * this.relativeFontScale) / 2;

    return Math.abs(Math.sin(maxHalfAngle)) * glyphHalfSize;
  }

  /**
   * Checks if a line contains a descender
   * A descender is the dangly part of a letter, like in g, y q etc.
   *
   * @param {string} line the contents of the line to check
   * @returns {boolean}
   */
  containsDescenders(line) {
    if (!line) {
      return false;
    }

    if (line.match(/[gpjqy]/g)) {
      return true;
    }

    return false;
  }

  measureText(line, verbose = false) {
    if (!line) {
      return 0;
    }

    const canvas = SierraCanvas.global;
    canvas.font = [
      this.fontStyle,
      this.fontWeight,
      `${this.fontSize}px`,
      this.font,
    ]
      .filter(Boolean)
      .join(' ');
    if (verbose) {
      return canvas.measureText(line);
    }

    if (this.textDirection === 'rtl' && !this.letterSpacing) {
      return Math.max(0, canvas.measureText(line).width);
    }

    const characters = this.getLineCharacters(line);
    const charactersWidth = characters.reduce(
      (total, character) => total + canvas.measureText(character).width,
      0
    );

    return Math.max(
      0,
      charactersWidth + this.letterSpacing * Math.max(0, characters.length - 1)
    );
  }

  /**
   * Event handler for when a user types and this element is active. Update the
   * text property of this element to match the contents of the textarea
   *
   * @return {void}
   */
  onInput() {
    this.updateLines(this.hiddenTextEditor.value.split('\n'));
    this.text = this.hiddenTextEditor.value;
    this.cursorPosition = this.hiddenTextEditor.selectionStart;
    this.controller?.render?.();
  }

  /**
   * Event handler for when a user releases a key while this element is active
   * Resets the cursor blink so that you know where your cursor is when it moves
   *
   * @return {void}
   */
  onKeyUp() {
    this.highlight[0] = this.hiddenTextEditor.selectionStart;
    this.highlight[1] = this.hiddenTextEditor.selectionEnd;
    this.resetCursorBlink();
    if (this.editMode) {
      return true;
    }
    return false;
  }

  /**
   * Event handler for when a user presses a key down. Immediately remove any
   * active highlight
   *
   * @return {void}
   */
  onKeyDown(e) {
    super.onKeyDown(e);
    this.highlight = [];
  }

  /**
   * Add extra postable parameters specific to this class
   *
   * @param {Object} post The default post parameters provided by getPostableParams
   * @see getPostableParams
   * @return {void}
   */
  extendPostableParams(post) {
    post.font = this.font;
    post.fontWeight = this.fontWeight;
    post.letterSpacing = this.letterSpacing;
    post.lineHeight = this.lineHeight;
    post.text = this.text;
    post.textAlign = this.textAlign;
    post.gradient = this.gradient || null;
  }

  /**
   * When this element is selected, begin flashing the cursor
   *
   * @return {void}
   */
  onSelect() {
    this.bounce('cursorOpacity', 0.5, 0, 600);
  }

  /**
   * When this element is deselected, cancel the flashing cursor, and perform
   * other cleanup tasks
   *
   * @return {void}
   */
  onDeselect() {
    this.lerpingVars = [];
    this.selected = false;
    this.editMode = false;
    this.highlight = [];
    this.deactivateTextEditor();
    this.clearBounces();

    if (this.perspectiveMode) {
      this.togglePerspectiveMode();
    }
  }
  bounces = {};

  /**
   * Lerp a property between two values, and once it reaches the second value
   * reverse the lerp. Repeat
   *
   * @param {String} variable The name of the variable to bounce
   * @param {Number} start The initial value
   * @param {Number} end The end value
   * @param {Number} loopTime The time to perform half a bounce
   * @return {void}
   */
  bounce(variable, start, end, loopTime) {
    // eslint-disable-next-line
    const master = this;
    if (this.bounces[variable]) {
      clearInterval(this.bounces[variable]);
    }
    this.bounces[variable] = setInterval(() => {
      master[variable] = master[variable] === start ? end : start;
      master.controller.render();
    }, loopTime);
  }

  /**
   * Remove all bouncing lerp animations
   *
   * @return {void}
   */
  clearBounces() {
    Object.keys(this.bounces).forEach(i => {
      clearInterval(this.bounces[i]);
    });
  }

  /**
   * Find which text character the user has clicked on when they click on the
   * element
   *
   * @param {Event} e The mouse event
   * @return {Number} The index of the selected character
   */
  getCursorPositionAtMousePosition(e) {
    let clickPosition = new Vector(e.x, e.y).subtract(this.position);

    const outputLines = this.lines;

    if (this.parent) {
      if (this.parent.className === 'VerticalGroup') {
        clickPosition.x +=
          ((this.parent.width - this.width) / 2) * (this.canvasScale - 1);
      }
      if (this.parent.className === 'HorizontalGroup') {
        clickPosition.y +=
          ((this.parent.height - this.height) / 2) * (this.canvasScale - 1);
      }
    }

    clickPosition = clickPosition.rotateAround(0, 0, this.angle);
    const relativeClickYPosition = clickPosition.y / this.height;

    const selectedLine = Mathf.clamp(
      Math.floor(this.lines.length * relativeClickYPosition),
      0,
      this.lines.length - 1
    );

    const outputLine = outputLines[selectedLine];
    const line = this.lines[selectedLine];

    if (!line) {
      return 0;
    }

    const lineWidth = this.measureText(outputLine);

    let relativeClickXPosition = 0;

    if (this.textAlign === 'center') {
      relativeClickXPosition =
        (clickPosition.x - (this.width - lineWidth) / 2) / lineWidth;
    } else if (this.textAlign === 'left') {
      relativeClickXPosition = clickPosition.x / lineWidth;
    } else if (this.textAlign === 'right') {
      relativeClickXPosition =
        (clickPosition.x - (this.width - lineWidth)) / lineWidth;
    }

    let cursorPosition = Mathf.clamp(
      Math.round(line.length * relativeClickXPosition),
      0,
      line.length
    );

    Object.keys(this.lines).forEach(i => {
      if (i >= selectedLine) {
        return;
      }
      cursorPosition += this.lines[i].length + 1;
    });

    return cursorPosition;
  }

  /**
   * Find the current active line
   *
   * @return {String} The active line
   */
  getActiveLine() {
    return this.lines[this.activeLineIndex] || '';
  }

  /**
   * Overwrite the current line with a new string
   *
   * @param {String} text The new line
   */
  setActiveLine(text) {
    this.lines[this.activeLineIndex] = text;
  }

  /**
   * Append some text to the element
   *
   * @param {String} text The new text
   */
  addText(text) {
    // alphanumeric character
    let activeLine = this.getActiveLine();
    if (this.cursorPosition !== false) {
      activeLine =
        activeLine.slice(0, this.cursorPosition) +
        text +
        activeLine.slice(this.cursorPosition);
      this.cursorPosition++;
    } else {
      activeLine += text;
    } // String.fromCharCode(e.which);
    this.setActiveLine(activeLine);
  }

  /**
   * Append some text to the element
   *
   * @param {String} text The new text
   */
  setText(text) {
    if (!text) {
      text = '';
    }
    this.getRealDimensions();
    const originalWidth = this.width;

    this.textDirection = getTextDirection(text);

    this.text = text;
    this.updateLines(text.split('\n'));

    this.getRealDimensions();
    const newWidth = this.width;

    const translationVector = new Vector(0, 0);

    switch (this.textAlign) {
      case 'right':
        translationVector.y =
          (Math.sin(this.angle * (Math.PI / 180)) *
            (newWidth - originalWidth)) /
          2;
        translationVector.x =
          Math.max(0, Math.cos(this.angle * (Math.PI / 180))) *
            (originalWidth - newWidth) -
          translationVector.y *
            Math.sign(translationVector.y) *
            Math.sign(newWidth - originalWidth);
        break;
      case 'center':
        translationVector.x = (originalWidth - newWidth) / 2;
        break;
      case 'left':
      default:
        translationVector.y =
          (Math.sin(this.angle * (Math.PI / 180)) *
            (originalWidth - newWidth)) /
          2;
        translationVector.x =
          Math.min(0, Math.cos(this.angle * (Math.PI / 180))) *
            (newWidth - originalWidth) -
          translationVector.y *
            Math.sign(translationVector.y) *
            Math.sign(newWidth - originalWidth);
        break;
    }

    this.translate(translationVector.x, translationVector.y);
  }

  updateLines(lines) {
    if (!Array.isArray(lines)) {
      if (typeof lines === 'string') {
        lines = [lines];
      } else {
        lines = [];
      }
    }

    this.lines = lines;
  }

  /**
   * Custom event handler for when scaling an object to instead increase or
   * decrease the fontsize
   *
   * @param {Number} distance @see rotScaleScale in OmniObject
   */
  rotScaleScale(distance) {
    const rotScale = this.controls.rotScale;
    this.angle = rotScale.startAngle;
    this.width =
      rotScale.startWidth +
      distance * rotScale.aspectRatio.x * (1 / this.canvasScale);
    this.fontSize =
      rotScale.startHeight +
      distance *
        (rotScale.aspectRatio.y / this.lines.length) *
        (1 / this.canvasScale);

    const translationVector = new Vector(0, 0)
      .rotateAround(
        rotScale.aspectRatio.normalize().x / 2,
        rotScale.aspectRatio.normalize().y / 2,
        -this.angle
      )
      .scale(distance);

    this.position.x =
      rotScale.startPosition.x - translationVector.x / this.canvasScale;
    this.position.y =
      rotScale.startPosition.y - translationVector.y / this.canvasScale;

    if (this.width < 0) {
      this.angle += 90;
      this.width *= -1;
      this.position.x -= this.width;
    }
    if (this.fontSize < 0) {
      this.angle += 90;
      this.fontSize *= -1;
      this.position.y -= this.fontSize;
    }
  }

  /**
   * Reset the bounce lerp animation for the cursor to full opacity
   *
   * @return {void}
   */
  resetCursorBlink() {
    const bounce = this.bounces.cursorOpacity;
    if (bounce) {
      clearInterval(bounce);
      this.select();
    }
    this.controller?.render?.();
  }

  /**
   * When editing text, allow the default move behaviour to instead highlight
   * a block of text
   *
   * @param {Vector} from The current position
   * @param {Vector} to The position we want to move to
   * @return {Boolean}
   */
  customMoveFunction(from, to) {
    if (
      this.mouseDownPosition &&
      typeof this.mouseDownPosition.distanceTo !== 'function'
    ) {
      this.mouseDownPosition = from;
    }

    if (this.mouseDownPosition && this.mouseDownPosition.distanceTo(to) > 5) {
      this.mouseDownPosition = false;
    }

    if (!this.editMode) {
      return false;
    }

    const cursorPosition = this.getCursorPositionAtMousePosition(to);
    this.highlight[1] = cursorPosition;

    return true;
  }

  afterScale() {
    this._updatePerspectiveCanvas();
  }

  /**
   * The custom event handler for when a user mousedowns on this element
   * Activate the text editor when clicking an already active text element,
   * unless the user is attempting to move or otherwise manipulate the element
   *
   * @param {Event} e The mouse down event
   * @return {void}
   */
  mouseDown(e) {
    // make sure that the current event matches the event when the object was first selected
    // otherwise, a touch event will trigger mousedown twice in some browsers (once for touch,
    // once for mousedown), resulting in an instant double click

    if (
      this.selected &&
      this.selectedEvent &&
      e.e.type === this.selectedEvent.type
    ) {
      this.mouseDownPosition = e;
    }

    if (this.selected && !this.selectedEvent) {
      this.selectedEvent = e.e;
    }

    let cursorPosition = this.getCursorPositionAtMousePosition(e);
    this.highlight = [cursorPosition, cursorPosition];

    if (!this.selected) {
      this.selected = true;
      this.selectedEvent = e.e;
    } else if (this.editMode) {
      cursorPosition = this.getCursorPositionAtMousePosition(e);
      this.hiddenTextEditor.setSelectionRange(cursorPosition, cursorPosition);
      // this.cursorPosition = 0;
      this.cursorPosition = this.hiddenTextEditor.selectionStart;
      this.lerp('cursorOpacity', 1, 0, 0.5, true);
      this.activateTextEditor(e);
    }
  }

  /**
   * The custom event handler for mouse up
   * Used to activate the edit mode of the text element
   *
   * @param {Event} e The browser event
   * @return {void}
   */
  mouseUp(e) {
    if (
      this.selected &&
      this.mouseDownPosition &&
      typeof this.mouseDownPosition.distanceTo === 'function' &&
      this.mouseDownPosition.distanceTo(e) < 5
    ) {
      this.editMode = true;
      this.controller.onElementActivated(this);
      this.cursorPosition = this.getCursorPositionAtMousePosition(e);
    }
    if (this.editMode) {
      const highlightPositions = this.highlight
        .slice(0, 2)
        .sort((a, b) => a - b);
      this.hiddenTextEditor.setSelectionRange(
        highlightPositions[0],
        highlightPositions[1]
      );
      this.activateTextEditor(e);
    }
  }

  /**
   * When editing the text, render a custom bounding box to communicate that
   * we are now in a different mode
   *
   * @return {Boolean}
   */
  customBounds() {
    this.ctx.rotateAroundPoint(
      this.radianAngle(-this.angle),
      this.width / 2,
      this.height / 2
    );
    if (!this.editMode) {
      return false;
    }

    this.ctx
      .beginPath()
      .rect(0, 0, this.width, this.height)
      .setGlobalAlpha(0.4)
      .setStrokeStyle('blue')
      .stroke()
      .closePath();

    return true;
  }

  onBeforeDelete() {
    this.baseLayerCanvas?.destroy();
    this.temporaryCanvas?.destroy();
    this.outputCanvas?.destroy();

    if (this.perspectiveCanvas) {
      this.perspectiveCanvas.destroy();
    }

    // release the renderer canvas from memory
    this.ctx = null;
    this.cnv = null;
  }

  /**
   * Custom event handler for the delete button to identify if the user is
   * attempting to delete the element or just delete some text
   *
   * @param {Event} e The OmniCanvasEvent
   *
   * @return {void}
   */
  delete(e) {
    if (!this.selectedErasable(e)) {
      this.controller.deleteObject(this);
    }

    const target = e?.originalTarget || e?.target;

    if (target && target === this.hiddenTextEditor) {
      this.controller.deleteObject(this);
    }
  }

  /**
   * Returns an object that represents all the differences between this element
   * and the base element.
   *
   * @return {Object}
   */
  toDelta() {
    const original = new this.constructor();
    const delta = this.controller.comparePropertiesInObjects(this, original, [
      'highlight',
      'internalCanvas',
      'selectedEvent',
      'mouseDownPosition',
      'cursorOpacity',
      'cursorPosition',
      'hiddenTextEditor',
      'baseControls',
      'perspectiveMode',
      'perspectiveCanvas',
      '_fontLoaded',
      'outputCanvas',
      'baseLayerCanvas',
      'activeDrawingCanvas',
      'temporaryCanvas',
    ]);
    delta.className = this.className;
    const boundingSubset = this._getBoundingSubsetData();
    if (boundingSubset) {
      delta.boundingSubset = boundingSubset;
    }
    return delta;
  }

  font = 'Lato, Helvetica, sans-serif';
  fontWeight = false;
  fontStyle = false;
  lineHeight = 1.0;
  text = 'Hello World';
  lines = [];
  textAlign = 'center';
  type = 'Text';
  editMode = false;
  letterSpacing = 0;
  color = '#000000';
  cursorPosition = false;
  activeLineIndex = 0;
  cursorOpacity = 0.5;
  customCursor = el => (el.editMode ? 'text' : 'all-scroll');
  selected = false;
  highlight = [];
  highlightHex = '#64b5f6';
  mouseDownPosition = false;
  blockMode = false;

  textImageSourceUrl = null;
  textImageReferenceId = null;

  horizontalSkewing = 0;
  verticalSkewing = 0;

  _fontLoaded = false;
}
